import { atom, selector, waitForNone } from "recoil";
import _ from "lodash";
import { API, graphqlOperation } from "aws-amplify";
import { listAlarmAlerts, listAlarmGroupsTable, listDevicesAlarmGroup } from "../graphql/queries-custom";
import { selectedUserSchemeAtom } from "./user";
import { tenantAccessibleResourcesSelector } from "./tenant";

export const selectedAlarmGroupAtom = atom({
	key: "selectedAlarmGroupAtom",
	default: null,
});

export const alarmGroupListAtom = atom({
	key: "alarmGroupListAtom",
	default: null,
});

export const alarmGroupListSelector = selector({
	key: "alarmGroupListSelector",
	get: async ({ get }) => {
		const scheme = get(selectedUserSchemeAtom);
		if (!scheme?.id) {
			return [];
		}

		let alarmGroupData = [];
		try {
			const [alarmGroupsResponse, devicesResponse] = await Promise.all([
				API.graphql(graphqlOperation(listAlarmGroupsTable, { filter: { schemeId: { eq: scheme.id } } })),
				API.graphql(graphqlOperation(listDevicesAlarmGroup, { schemeId: scheme.id })),
			]);

			const {
				data: {
					listAlarmGroups: { items },
				},
			} = alarmGroupsResponse;
			const {
				data: {
					deviceByScheme: { items: devices },
				},
			} = devicesResponse;

			alarmGroupData = items ?? [];
			const devicesById = new Map(
				(devices ?? []).filter((device) => device?.id).map((device) => [device.id, device]),
			);

			alarmGroupData.forEach((group) => {
				const inputItems = group?.input?.items ?? [];
				const outputItems = group?.output?.items ?? [];
				const inputDeviceIds = inputItems.map((input) => input?.deviceID).filter(Boolean);
				const outputDeviceIds = outputItems.map((output) => output?.deviceID).filter(Boolean);
				const inputDevices = inputDeviceIds.map((deviceId) => devicesById.get(deviceId)).filter(Boolean);
				const outputDevices = outputDeviceIds.map((deviceId) => devicesById.get(deviceId)).filter(Boolean);
				const hasOrphanedRelations =
					inputDevices.length !== inputDeviceIds.length || outputDevices.length !== outputDeviceIds.length;

				group.inputCount = inputDeviceIds.length;
				group.outputCount = outputDeviceIds.length;
				let espComplete = false;

				for (const input of inputDevices) {
					espComplete = checkPeerCompletion(input, outputDeviceIds);

					//If this isn't true there is no need to continue
					if (!espComplete) break;
				}

				for (const output of outputDevices) {
					espComplete = checkPeerCompletion(output, inputDeviceIds);

					//If this isn't true there is no need to continue
					if (!espComplete) break;
				}

				group.espComplete = hasOrphanedRelations ? false : espComplete;
			});
		} catch (err) {
			throw new Error(getErrorMessage(err));
		}

		return alarmGroupData;
	},
	set: ({ set }, newValue) => {
		set(alarmGroupListAtom, newValue);
	},
});

const getErrorMessage = (err) => {
	if (Array.isArray(err?.errors) && err.errors.length > 0) {
		return err.errors
			.map((e) => e?.message)
			.filter(Boolean)
			.join(", ");
	}
	if (typeof err?.message === "string" && err.message.length > 0) {
		return err.message;
	}
	if (typeof err === "string") {
		return err;
	}

	try {
		return JSON.stringify(err);
	} catch {
		return "Unknown error";
	}
};

const parseReportedState = (reportedState) => {
	if (!reportedState) {
		return {};
	}

	if (typeof reportedState === "object") {
		return reportedState;
	}

	if (typeof reportedState === "string") {
		try {
			return JSON.parse(reportedState);
		} catch {
			return {};
		}
	}

	return {};
};

const getComparableDeviceIds = (id) => {
	const normalisedId = typeof id === "string" ? id.toLowerCase() : "";
	if (!normalisedId) {
		return [];
	}

	const suffix = normalisedId.includes("_") ? normalisedId.slice(normalisedId.indexOf("_") + 1) : normalisedId;
	return [normalisedId, suffix, `andel_${suffix}`, `cc_${suffix}`, `fs2_${suffix}`];
};

const checkPeerCompletion = (currentDevice, comparisonIds) => {
	if (!currentDevice?.reported_state || !Array.isArray(comparisonIds) || comparisonIds.length === 0) {
		return false;
	}

	//Get the devices currently configured peers which are outputs for this group
	const reportedStateObj = parseReportedState(currentDevice.reported_state);
	const peers = Array.isArray(reportedStateObj?.ESPNow?.peers) ? reportedStateObj.ESPNow.peers : [];
	if (peers.length === 0) {
		return false;
	}

	const comparableIds = new Set(comparisonIds.flatMap((comparisonId) => getComparableDeviceIds(comparisonId)));
	const espPeers = peers.filter((peer) => {
		const peerId = typeof peer?.id === "string" ? peer.id.toLowerCase() : "";
		return comparableIds.has(peerId);
	});

	//Check there are enough peers
	if (espPeers.length < comparisonIds.length) {
		return false;
	}

	//Make sure all the peers are paired
	return espPeers.every((peer) => Boolean(peer?.paired));
};

export const deviceSensorAlarmListAtom = atom({
	key: "deviceSensorAlarmListAtom",
	default: [],
});

export const deviceSensorAlarmListSelector = selector({
	key: "deviceSensorAlarmListSelector",
	get: async ({ get }) => {
		let sensorAlarms;
		const alarmList = get(deviceSensorAlarmListAtom);
		const selectedUserScheme = get(selectedUserSchemeAtom);

		if (alarmList.length === 0) {
			const {
				data: {
					deviceSensorAlertBySensorState: { items: sensorAlarmItems },
				},
			} = await API.graphql(
				graphqlOperation(listAlarmAlerts, {
					sensor_state: 1,
					filter: { schemeId: { eq: selectedUserScheme.id } },
				}),
			);

			// Drop alarms with no linked device - they cannot be identified or rendered (AlarmCard needs device.id/name/zone)
			const activeSensorAlarmItems = sensorAlarmItems.filter(
				(alarm) => Number(alarm?.sensor_state) > 0 && alarm?.device,
			);

			for await (const sensorAlarmItem of activeSensorAlarmItems) {
				const outputDevices = [];
				const alarmGroupInputs = sensorAlarmItem.device?.alarmGroupsInput?.items ?? [];

				for await (const alarmGroupInput of alarmGroupInputs) {
					const outputItems = alarmGroupInput?.alarmGroup?.output?.items ?? [];
					for await (const outputDevice of outputItems) {
						if (outputDevice?.device) outputDevices.push(outputDevice.device);
					}
				}

				sensorAlarmItem.outputDevices = _.uniqBy(outputDevices, "id");
			}

			sensorAlarms = activeSensorAlarmItems;
		} else {
			sensorAlarms = alarmList;
		}

		if (selectedUserScheme?.role === "TENANT") {
			const { deviceIds } = await get(tenantAccessibleResourcesSelector);
			sensorAlarms = sensorAlarms.filter((alert) => deviceIds?.includes(alert.device?.id));
		}

		return sensorAlarms;
	},
});

export const deviceSensorAlarmCounterSelector = selector({
	key: "alarmCounterSelector",
	get: ({ get }) => {
		const scheme = get(selectedUserSchemeAtom);
		const alarmList = get(deviceSensorAlarmListAtom);

		if (scheme?.role !== "TENANT") {
			return alarmList.length;
		}

		const [filteredListLoadable, accessibleResourcesLoadable] = get(
			waitForNone([deviceSensorAlarmListSelector, tenantAccessibleResourcesSelector]),
		);

		if (accessibleResourcesLoadable.state !== "hasValue") {
			return 0;
		}

		const { deviceIds } = accessibleResourcesLoadable.contents ?? {};
		if (!deviceIds?.length) {
			return 0;
		}

		if (filteredListLoadable.state === "hasValue") {
			return filteredListLoadable.contents.length;
		}

		return alarmList.filter((alert) => deviceIds.includes(alert.device?.id)).length;
	},
});
